import { keyframes } from "@mui/system";

// HomeHero motion. Compositor-only (transform + opacity), one-shot `both` animations, every one
// guarded for reduced motion where it's applied. Values are the transitions.dev motion tokens
// (named in the comments) except where CLAUDE.md overrides them: the entrance curve, and the
// hero-length entrance duration.
//
// Entrance: transitions.dev's "Texts reveal" (#18) — lines rise --distance-medium (12px) and
// fade in, staggered so the eye lands on the headline first — on the project entrance curve and
// a 900ms hero duration instead of the recipe's 500ms, and with no `filter: blur` (not
// compositor-only). Four beats, --duration-micro (80ms) apart: 240ms of stagger in all, under
// the ~300ms after which the last line feels late. The two columns enter side by side — the
// map's overlays share the copy column's beats — so the hero lands as one sweep.
//
// Land swap: a carousel swap is a "page slide" — --duration-fast (250ms), --distance-base
// (8px), --duration-stagger (40ms) — still on the entrance curve. It's frequent and
// user-triggered, so it answers at once instead of replaying the page-load choreography. The
// new land's lines slide in from the side it came from (from the right for the next land, the
// left for the previous), so a swipe or a later dot reads as moving forward through the set.
//
// Images: the static map and the land photo fade from the black placeholder up to the design's
// 80% opacity once loaded: a "Skeleton reveal" (#14), --duration-slow (400ms), minus the blur.
// On a swap the new map fades over the old one, so this is also the cross-fade.
//
// No `will-change`: these are short one-shot transform/opacity animations, which the browser
// promotes for their duration and releases after — nothing is left on a layer permanently.

export const heroRise = keyframes`
  from { opacity: 0; transform: translate3d(0, 12px, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
`;

export const heroSwapFromRight = keyframes`
  from { opacity: 0; transform: translate3d(8px, 0, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
`;

export const heroSwapFromLeft = keyframes`
  from { opacity: 0; transform: translate3d(-8px, 0, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
`;

// Ends at the images' resting 0.8 (the design's black-under-80% treatment).
export const heroImageIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 0.8; }
`;

export const HERO_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
/** Page-load entrance per line (CLAUDE.md hero length). */
export const HERO_ENTER_MS = 900;
/** Between entrance beats (--duration-micro, the "large stagger"). */
export const HERO_STAGGER_MS = 80;
/** A land swap's rise (--duration-fast, page slide). */
export const HERO_SWAP_MS = 250;
/** Between swap lines (--duration-stagger). */
export const HERO_SWAP_STAGGER_MS = 40;
/** Image reveal once loaded (--duration-slow, skeleton content reveal). */
export const HERO_IMAGE_MS = 400;
/** Active-dot slide: "Tabs sliding" (--duration-fast + --ease-smooth-out), same both ways. */
export const HERO_DOT_MS = 250;
export const HERO_DOT_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

export const reducedMotionNone = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const;

/** "enter" on page load; "next"/"prev" for a land swap in that direction. */
export type HeroMotionMode = "enter" | "next" | "prev";

/** sx/styled fragment for one staggered line. `step` is its beat in the sequence (0–3). */
export const riseSx = (step: number, mode: HeroMotionMode) => {
  const swapIn = mode === "prev" ? heroSwapFromLeft : heroSwapFromRight;
  return {
    animation:
      mode === "enter"
        ? `${heroRise} ${HERO_ENTER_MS}ms ${HERO_EASE} ${step * HERO_STAGGER_MS}ms both`
        : `${swapIn} ${HERO_SWAP_MS}ms ${HERO_EASE} ${step * HERO_SWAP_STAGGER_MS}ms both`,
    ...reducedMotionNone,
  };
};

export const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
